/**
 * Minimal routing.
 *
 * Screens were previously chosen by component state, which meant the address
 * bar said /login no matter where you were: no bookmarking, no back button, no
 * way to send someone a link to a section, and a refresh always dumped you at
 * the start.
 *
 * A router library is not warranted for a handful of flat paths. What is
 * warranted is that every screen has a real URL and that history works.
 */

import { useEffect, useState } from 'react';

/** Paths that do not require a session. */
export const PUBLIC_PATHS = ['/login', '/register', '/verify'];

/** Parent sections, in sidebar order. `path` is the URL, `id` the section key. */
export const PARENT_SECTIONS = [
  { id: 'overview', path: '/', label: 'نظرة عامة' },
  { id: 'students', path: '/students', label: 'الطلاب' },
  { id: 'lessons', path: '/lessons', label: 'الدروس' },
  { id: 'skills', path: '/skills', label: 'المهارات' },
  { id: 'curriculum', path: '/curriculum', label: 'المنهج' },
];

/** Normalizes a pathname: strips a trailing slash, keeps the root as "/". */
export function normalizePath(pathname) {
  const trimmed = String(pathname || '/').replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/**
 * Subscribes to the current location.
 *
 * Returns the normalized path, the query string, and the two navigation verbs.
 * `replace` exists for redirects: sending a signed-in parent from /login to the
 * dashboard should not leave /login in history for the back button to return to.
 */
export function useLocation() {
  const [location, setLocation] = useState(() => ({
    path: normalizePath(window.location.pathname),
    search: window.location.search,
  }));

  useEffect(() => {
    const sync = () =>
      setLocation({
        path: normalizePath(window.location.pathname),
        search: window.location.search,
      });
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  function go(to, { replace = false } = {}) {
    const current = `${window.location.pathname}${window.location.search}`;
    if (to === current) return;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', to);
    setLocation({
      path: normalizePath(window.location.pathname),
      search: window.location.search,
    });
  }

  return {
    path: location.path,
    search: location.search,
    navigate: (to) => go(to),
    replace: (to) => go(to, { replace: true }),
  };
}

/** Reads one query parameter from a search string. */
export function queryParam(search, name) {
  return new URLSearchParams(search).get(name);
}
